import * as T from 'three'
import type { Bed } from './beds'
import { customGeometry, customStyle, isCustomLayout } from './custom-layout'

export const CABIN_CROWN_HEIGHT = 7
export function supportsCabinCrown(bed: Bed) {
  return isCustomLayout(bed) && customStyle(bed) === 'divider'
}

/** Continuous front crown across the enclosed cabins, including their center filler.
 * Seven-inch installed vertical face; sits BELOW the existing ceiling plane.
 * Its own frame material participates in all paint, stain and raw-wood changes.
 */
export function addCabinCrown(root: T.Group, bed: Bed, ceiling: number) {
  if (!supportsCabinCrown(bed) || !Number.isFinite(ceiling) || ceiling < CABIN_CROWN_HEIGHT) return
  const { uppers } = customGeometry(bed)
  const left = Math.min(...uppers.map(r => r.x))
  const right = Math.max(...uppers.map(r => r.x + r.w))
  const front = Math.max(...uppers.map(r => r.y + r.h))
  const profile = new T.Shape()
  const point = (x: number, y: number) => [x / 12, y / 12] as const
  profile.moveTo(...point(0, 0))
  profile.lineTo(...point(.45, 0))
  profile.lineTo(...point(.45, .65))
  profile.bezierCurveTo(.5/12, 1.5/12, 2.4/12, 1.9/12, 2.45/12, 4.8/12)
  profile.lineTo(...point(2.65, 5.25))
  profile.lineTo(...point(2.65, 6.15))
  profile.lineTo(...point(3, 6.15))
  profile.lineTo(...point(3, CABIN_CROWN_HEIGHT))
  profile.lineTo(...point(0, CABIN_CROWN_HEIGHT))
  profile.closePath()
  const mesh = new T.Mesh(
    new T.ExtrudeGeometry(profile, { depth: (right-left)/12, bevelEnabled: false, curveSegments: 16 }),
    new T.MeshStandardMaterial({ color: 0xffffff, roughness: .72 })
  )
  mesh.name = 'cabin-crown-7-front'
  mesh.userData.finishPart = 'frame'
  mesh.userData.installedHeightInches = CABIN_CROWN_HEIGHT
  mesh.rotation.y = -Math.PI / 2
  mesh.position.set((right-bed.width/2)/12, (ceiling-CABIN_CROWN_HEIGHT)/12, (front-bed.depth/2)/12)
  mesh.castShadow = true
  mesh.receiveShadow = true
  root.add(mesh)
}
